/**
 * Install step 2 — the page's Content-Security-Policy, checked for the
 * partner instead of explained to them. The API fetches the page (tier 1's
 * guarded fetch) and compares its policy with the sources this portal
 * renders (`cspRequirements`, live mode only when it is on):
 *
 *   no policy        → one line, nothing to do
 *   policy allows it → a check mark
 *   policy blocks it → only the missing directives, paste-ready
 *
 * With no website seen yet there is nothing to fetch, so the full snippet sits
 * in a collapsed "Does your site use a CSP?" disclosure.
 */
import { useState, type ReactNode } from 'react';
import type { ApiError } from '../api/errors';
import { useSession } from '../auth/session';
import { API_BASE } from '../env';
import { CodeBlock, Loading } from '../ui/kit';
import { useQuery } from '../ui/hooks';
import { cspCaveats, cspRequirements, cspSnippet, missingCspSnippet } from './snippets';
import { useCheckTarget } from './target';

export function CspHelp({ live }: { live: boolean }) {
  return (
    <details className="help">
      <summary>{live ? 'Nonce or hash policies, older Safari, live conversation' : 'Nonce or hash policies, older Safari'}</summary>
      <ul>
        {cspCaveats({ live }).map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </details>
  );
}

function FullSnippet({ live, chat = false }: { live: boolean; chat?: boolean }) {
  return (
    <div className="stack-sm">
      <CodeBlock wrap caption={live ? 'Add to your CSP · with live conversation' : chat ? 'Add to your CSP · with chat' : 'Add to your CSP'} code={cspSnippet(API_BASE, { live, chat })} />
      <CspHelp live={live} />
    </div>
  );
}

function CspDisclosure({
  live,
  chat = false,
  title = 'Does your site use a Content-Security-Policy?',
}: {
  live: boolean;
  chat?: boolean;
  title?: string;
}) {
  return (
    <details className="disclosure">
      <summary>{title}</summary>
      <div className="stack-sm disclosure-body">
        <p className="small muted">Most sites don’t. If yours sends one, add these sources to it or the widget won’t load.</p>
        <FullSnippet live={live} chat={chat} />
      </div>
    </details>
  );
}

const UNREACHABLE: Record<string, string> = {
  http_status: 'the page answered with an error',
  timeout: 'the page took too long to answer',
  redirected_off_site: 'the page redirected to another website',
};

function StatusLine({ tone, children }: { tone: 'ok' | 'bad' | 'neutral' | 'pending'; children: ReactNode }) {
  return (
    <p className={`status-line ${tone}`}>
      <span className={`status-icon ${tone}`} aria-hidden="true" />
      <span className="grow">{children}</span>
    </p>
  );
}

export function CspSection({
  siteKey,
  origins,
  live,
  chat = false,
  configLoading,
}: {
  siteKey: string;
  /** The site's websites (blocked ones excluded). */
  origins: string[];
  live: boolean;
  /** Spec 014: chat needs the LiveKit hosts, without media-src. */
  chat?: boolean;
  configLoading: boolean;
}) {
  const { api } = useSession();
  const target = useCheckTarget(siteKey, origins);
  const [rechecking, setRechecking] = useState(false);
  const check = useQuery(() => api.checkCsp(siteKey, target.url, cspRequirements(API_BASE, { live, chat })), [siteKey, target.url, live, chat], {
    enabled: Boolean(siteKey && target.url) && !configLoading,
  });
  const again = async () => {
    setRechecking(true);
    try {
      await check.reload();
    } finally {
      setRechecking(false);
    }
  };

  if (configLoading) return <Loading />;
  if (!target.url) return <CspDisclosure live={live} chat={chat} />;

  const pageLink = <span className="mono nowrap-soft">{target.url}</span>;
  const recheck = (
    <button type="button" className="linkbtn" onClick={() => void again()} disabled={rechecking || check.loading}>
      Check again
    </button>
  );
  const data = check.data;
  const busy = check.loading || rechecking;

  let body: ReactNode;
  if (busy) {
    body = <StatusLine tone="pending">Checking {pageLink} for a Content-Security-Policy…</StatusLine>;
  } else if (check.error || !data) {
    body = (
      <div className="stack-sm">
        <StatusLine tone="neutral">
          Couldn’t check {pageLink}: {errorText(check.error)} {recheck}
        </StatusLine>
        <CspDisclosure live={live} chat={chat} title="Add the sources yourself" />
      </div>
    );
  } else if (data.status === 'none') {
    body = (
      <StatusLine tone="ok">
        No Content-Security-Policy found on {pageLink}, nothing to do. {recheck}
      </StatusLine>
    );
  } else if (data.status === 'ok') {
    body = (
      <div className="stack-sm">
        <StatusLine tone="ok">
          Your Content-Security-Policy on {pageLink} already allows the widget. {recheck}
        </StatusLine>
        {data.strict_dynamic ? (
          <p className="small muted">
            Your script-src uses <code>'strict-dynamic'</code>: put your page’s nonce on the widget’s <code>&lt;script&gt;</code> tag.
          </p>
        ) : null}
      </div>
    );
  } else if (data.status === 'missing') {
    body = (
      <div className="stack-sm">
        <StatusLine tone="bad">
          Your Content-Security-Policy on {pageLink} blocks the widget. Add these sources to it. {recheck}
        </StatusLine>
        <CodeBlock wrap caption="Missing from your CSP" code={missingCspSnippet(data.missing)} />
        <CspHelp live={live} />
      </div>
    );
  } else {
    body = (
      <div className="stack-sm">
        <StatusLine tone="neutral">
          Couldn’t read {pageLink}: {data.message ?? UNREACHABLE[data.reason ?? ''] ?? 'the page could not be fetched'}
          {data.http_status ? ` (HTTP ${data.http_status})` : ''}. {recheck}
        </StatusLine>
        <CspDisclosure live={live} chat={chat} title="Add the sources yourself" />
      </div>
    );
  }

  return (
    <div className="stack-sm">
      {origins.length > 1 ? (
        <label className="row small" style={{ gap: 8 }}>
          <span className="muted">Website</span>
          <select className="input input-sm" value={target.origin} onChange={(e) => target.setOrigin(e.target.value)}>
            {origins.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div role="status" aria-live="polite">
        {body}
      </div>
    </div>
  );
}

function errorText(error: ApiError | null): string {
  const message = error?.message?.trim();
  if (!message) return 'the check did not run.';
  return /[.!?]$/.test(message) ? message : `${message}.`;
}
